import { jsx as _jsx } from "react/jsx-runtime";
import React, { useEffect, useImperativeHandle, useState } from 'react';
import axios from "axios";
import { useSessionStorage } from "usehooks-ts";
import { Table } from "antd";
import PAGINATION_DEFAULTS from '../../../../constants/PaginationDefaults';
import { axiosErrorHandler, conditionFilter, downloadAsFile, isNullOrUndefined, isNullOrUndefinedOrEmpty, showErrorNotification } from "../../../../swzutils";
import { userPermissionFilter } from "../../../../swzutils/UserPermissionsUtil";
import TextTableFilter, {} from "./TextTableFilter";
import DateRangeTableFilter from "./DateRangeTableFilter";
import DateTableFilter from "./DateTableFilter";
import NumberMinMaxTableFilter from "./NumberMinMaxTableFilter";
import { isDeepEqual } from "../../../../swzutils/TypeUtils";
import NumberTableFilter from "./NumberTableFilter";
import { useAppSelector } from "../../../../store";
import { loggedInUserSelector, selectedTenantSelector } from "../../../../store/slice/auth";
export var PaginatedFilterableTableExpandKey;
(function (PaginatedFilterableTableExpandKey) {
    PaginatedFilterableTableExpandKey["EXPAND_ALL"] = "EXPAND_ALL";
    PaginatedFilterableTableExpandKey["COLLAPSE_ALL"] = "COLLAPSE_ALL";
    PaginatedFilterableTableExpandKey["EXPAND_USING_DETERMINE_ROW_KEYS_CALLBACK"] = "EXPAND_USING_DETERMINE_ROW_KEYS_CALLBACK";
})(PaginatedFilterableTableExpandKey || (PaginatedFilterableTableExpandKey = {}));
const PaginatedFilterableTable = ({ tableKey, dataUri, className = "", rowClassName, size = 'middle', initialPageSize = PAGINATION_DEFAULTS.defaultPageSize, minWidthBeforeScrolling = 800, columns, components, onRow, initialFilters = {}, saveChangesInSessionStorage = true, loading = false, refreshOnSelectedTenantChanged = false, filterTenantId, onDataFetched, rowSelection, rowKey = (record) => record.key ? record.key : record.id, expandable, determineExpandedRowKeysCallback, initialExpandKey = PaginatedFilterableTableExpandKey.EXPAND_ALL, summary }, exportedRef) => {
    const [sessionPageSize, setSessionPageSize] = useSessionStorage("pagination_pageSize", initialPageSize);
    const [sessionPageOffset, setSessionPageOffset] = useSessionStorage(`${tableKey}_offset`, 0);
    // const [ sessionSortFilter, setSessionSortFilter ] = useSessionStorage<Record<string, string | null | undefined> | null | undefined>(`${tableKey}_sortFilter`, null);
    const [sessionFilters, setSessionFilters] = useSessionStorage(`${tableKey}_filters`, initialFilters);
    const [pageSize, setPageSize] = useState(saveChangesInSessionStorage ? sessionPageSize : initialPageSize);
    const [pageOffset, setPageOffset] = useState(saveChangesInSessionStorage ? sessionPageOffset : 0);
    const [filters, setFilters] = useState(saveChangesInSessionStorage ? sessionFilters : initialFilters);
    const [sortFilter, setSortFilter] = useState(null); // saveChangesInSessionStorage ? sessionSortFilter : null);
    const [showSpinner, setShowSpinner] = useState(false);
    const [pagination, setPagination] = useState({ ...PAGINATION_DEFAULTS, defaultPageSize: pageSize });
    const [openFilterKey, setOpenFilterKey] = useState(undefined);
    const [data, setData] = useState([]);
    const [expandedRowKeys, setExpandedRowKeys] = useState([]);
    const loggedInUser = useAppSelector(loggedInUserSelector);
    const selectedTenant = useAppSelector(selectedTenantSelector);
    const [selectedTenantId, setSelectedTenantId] = useState(selectedTenant?.id);
    const [expandKey, setExpandKey] = useState(initialExpandKey);
    useImperativeHandle(exportedRef, () => ({
        getData: () => {
            return data;
        },
        fetch: () => {
            fetch();
        },
        downloadCurrentFilterAsCsv: (entityName, pathSuffix = "/csv", additionalQueryParams) => {
            downloadCurrentFilterAsCsv(entityName, pathSuffix, additionalQueryParams);
        },
        updateRecord: (record) => {
            const dataArray = data;
            const foundIndex = dataArray.findIndex(r => r.id === record.id);
            if (foundIndex >= 0) {
                const dataCopy = [...dataArray];
                dataCopy[foundIndex] = record;
                setData(dataCopy);
            }
        },
        addRecord: (record) => {
            setData([...data, record]);
        },
        addRecords: (records) => {
            setData([...data, ...records]);
        },
        prependRecord: (record) => {
            setData([record, ...data]);
        },
        removeRecordById: (recordId) => {
            const filteredArray = data.filter(r => r.id !== recordId);
            setData(filteredArray);
        },
        removeRecordsById: (recordIds) => {
            const filteredArray = data.filter(r => !recordIds.includes(r.id));
            setData(filteredArray);
        },
        clearAllFilters: () => {
            setFilters({});
            setPageOffset(0);
            setOpenFilterKey(undefined);
        },
        resetToInitialFilters: () => {
            setFilters(initialFilters);
            setPageOffset(0);
            setOpenFilterKey(undefined);
        },
        clearFilter: (requestKey) => {
            clearFilterKey(requestKey);
        },
        applyFilters: (newFilters) => {
            applyFilterKeys(newFilters);
        },
        applyFilter: (requestKey, value) => {
            applyFilterKey(requestKey, value);
        },
        getCurrentFilters: () => {
            return filters;
        },
        expandOrCollapse: (by) => {
            setExpandKey(by);
        },
        setRowKeyExpanded
    }));
    useEffect(() => {
        // console.log("useEffect..  dataUri:" + dataUri + " pageSize:" + pageSize + " pageOffset:" + pageOffset);
        if (saveChangesInSessionStorage) {
            // setSessionSortFilter(sortFilter);
            setSessionFilters(filters);
            setSessionPageSize(pageSize);
            setSessionPageOffset(pageOffset);
        }
        fetch();
    }, [dataUri, pageSize, pageOffset, filters, sortFilter, selectedTenantId]);
    useEffect(() => {
        // Need to use a wrapped state variable to prevent double server calls on init
        if (refreshOnSelectedTenantChanged && selectedTenant?.id !== selectedTenantId) {
            setSelectedTenantId(selectedTenant?.id);
            setPageOffset(0);
        }
    }, [refreshOnSelectedTenantChanged, selectedTenant?.id]);
    useEffect(() => {
        applyExpandedRowKeys(data);
    }, [expandKey]);
    const applyExpandedRowKeys = (records) => {
        if (expandKey == PaginatedFilterableTableExpandKey.EXPAND_USING_DETERMINE_ROW_KEYS_CALLBACK && determineExpandedRowKeysCallback) {
            setExpandedRowKeys(determineExpandedRowKeysCallback(records));
        }
        else if (expandKey == PaginatedFilterableTableExpandKey.EXPAND_ALL) {
            const allRowKeys = new Set(records.map(it => it.id));
            setExpandedRowKeys([...allRowKeys]);
        }
        else {
            setExpandedRowKeys([]);
        }
    };
    const setRowKeyExpanded = (rowKey, expanded) => {
        const rowKeys = new Set(expandedRowKeys);
        expanded ? rowKeys.add(rowKey) : rowKeys.delete(rowKey);
        setExpandedRowKeys([...rowKeys]);
    };
    const onRowExpand = (expanded, record) => {
        setRowKeyExpanded(record.id, expanded);
    };
    const onTableChange = (changedPagination, changedFilters, changedSorter, changedExtra) => {
        // console.log("onTableChange");
        // console.log("--pagination", changedPagination);
        // console.log("--filters", changedFilters);
        // console.log("--sorter", changedSorter);
        // console.log("--extra", changedExtra);
        if (changedExtra.action === 'paginate') {
            if (changedPagination.current && changedPagination.pageSize) {
                const offset = (changedPagination.current - 1) * changedPagination.pageSize;
                setPageOffset(offset);
            }
            else {
                setPageOffset(0);
            }
            setPageSize(changedPagination.pageSize);
        }
        if (changedExtra.action === 'filter') {
            Object.keys(changedFilters)
                .forEach((filterName) => filterName.startsWith("unused") && delete changedFilters[filterName]);
            const newFilters = {
                ...filters,
                ...changedFilters
            };
            // Delete any filter that is null.  This is because the server will treat a null boolean filter as false
            Object.keys(newFilters)
                .forEach((filterName) => (newFilters[filterName] === null) && delete newFilters[filterName]);
            //
            //console.log("onTableChange setFilters", JSON.stringify(filters));
            setFilters(newFilters);
        }
        if (changedExtra.action === 'sort') {
            const mapSorterToState = (sorter) => {
                if (!sorter?.column) {
                    setSortFilter(null);
                    return;
                }
                const col = sorter.column;
                if (!col?.sortFilter) {
                    console.error("Sorter changed but no sortFilter property is defined for column: " + col?.title);
                    return;
                }
                switch (sorter.order) {
                    case 'ascend':
                        setSortFilter({
                            [col.sortFilter.requestKey]: col.sortFilter.ascending
                        });
                        break;
                    case 'descend':
                        setSortFilter({
                            [col.sortFilter.requestKey]: col.sortFilter.descending
                        });
                        break;
                    default:
                        setSortFilter(null);
                        break;
                }
            };
            if (Array.isArray(changedSorter)) {
                const firstSorter = changedSorter[0];
                console.warn("Multiple sorters are not supported, only applying sorter " + firstSorter.columnKey);
                mapSorterToState(firstSorter);
            }
            else {
                mapSorterToState(changedSorter);
            }
        }
    };
    const updatePagination = (page) => {
        const resultPagination = { ...pagination };
        resultPagination.current = (page.limit > 0) ? ((page.offset / page.limit) + 1) : 1;
        resultPagination.current = (page.offset / page.limit) + 1;
        if (page.maxResults) {
            // PaginationResponse<RecordType>
            resultPagination.total = page.maxResults;
        }
        else {
            // IteratorResponse<RecordType>
            resultPagination.showTotal = (total, range) => `${range[0]}-${range[1]}`;
            resultPagination.total = page.hasMore ? (page.offset + page.limit + 1) : (page.offset + page.results.length);
        }
        return resultPagination;
    };
    const fetch = () => {
        setShowSpinner(true);
        const params = {
            offset: pageOffset,
            limit: pageSize,
            ...filters,
            ...sortFilter
        };
        // console.log("fetch");
        // console.log("--pagination", pagination);
        // console.log("--filters", filters);
        // console.log("--sortFilter", sortFilter)
        // console.log("--params", params);
        // console.log("--filterTenantId", filterTenantId);
        axios(dataUri, {
            method: "get",
            params: params,
            headers: filterTenantId ? { "FilterTenantId": filterTenantId } : undefined,
        }).then(response => {
            setPagination(updatePagination(response.data));
            setData(response.data.results);
            applyExpandedRowKeys(response.data.results);
            onDataFetched && onDataFetched(response.data.results);
        })
            .catch(axiosErrorHandler)
            .finally(() => setShowSpinner(false));
    };
    const downloadCurrentFilterAsCsv = (entityName, pathSuffix, additionalQueryParams) => {
        setShowSpinner(true);
        // Remove null values from the filter
        const params = {
            ...filters,
            ...additionalQueryParams
        };
        axios(dataUri + pathSuffix, {
            method: "get",
            params: params
        }).then(response => {
            if (response.data) {
                downloadAsFile(response.data, entityName + '.csv');
            }
            else {
                showErrorNotification('No ' + entityName + ' found for the current filters');
            }
        })
            .catch(axiosErrorHandler)
            .finally(() => setShowSpinner(false));
    };
    const applyFilterKey = (requestKey, value, close = true) => {
        if (value == null || value.length == 0 || isNullOrUndefinedOrEmpty(value[0])) {
            clearFilterKey(requestKey);
            return;
        }
        let newFilters = {
            ...filters,
            [requestKey]: value
        };
        if (!isDeepEqual(newFilters, filters)) {
            setFilters(newFilters);
            setPageOffset(0);
        }
        if (close) {
            setOpenFilterKey(undefined);
        }
    };
    const applyFilterKeys = (newFilters) => {
        let updatedFilters = {
            ...filters,
            ...newFilters
        };
        // Delete any filter that is null.  This is because the server will treat a null boolean filter as false
        Object.keys(updatedFilters).forEach(filterName => {
            if (updatedFilters[filterName] === null) {
                delete updatedFilters[filterName];
            }
        });
        if (!isDeepEqual(updatedFilters, filters)) {
            setFilters(updatedFilters);
            setPageOffset(0);
        }
        setOpenFilterKey(undefined);
    };
    const clearFilterKey = (requestKey, close = true) => {
        setFilters((prevFilters) => {
            const newFilter = { ...prevFilters };
            delete newFilter[requestKey];
            return newFilter;
        });
        setPageOffset(0);
        if (close) {
            setOpenFilterKey(undefined);
        }
    };
    const clearFilterKeys = (clearFilters) => {
        setFilters((prevFilters) => {
            const updatedFilters = { ...prevFilters };
            clearFilters.forEach(key => delete updatedFilters[key]);
            return updatedFilters;
        });
        setPageOffset(0);
        setOpenFilterKey(undefined);
    };
    const mapEnumsFilter = (col) => {
        const filter = col.filter;
        return {
            ...col,
            key: filter.requestKey,
            filters: filter.enums,
            filterMultiple: filter.filterMultiple,
            filteredValue: filters[filter.requestKey]
        };
    };
    const mapTextFilter = (col) => {
        const filter = col.filter;
        let presenceFilterOpts = null;
        if (filter.presenceFilter) {
            presenceFilterOpts = {
                filter: filter.presenceFilter,
                onApply: (isPresent) => {
                    let value = isPresent;
                    if (isPresent && !isNullOrUndefined(filter.presenceFilter?.requestValuePresent)) {
                        value = filter.presenceFilter.requestValuePresent;
                    }
                    if (!isPresent && !isNullOrUndefined(filter.presenceFilter?.requestValueAbsent)) {
                        value = filter.presenceFilter.requestValueAbsent;
                    }
                    applyFilterKey(filter.presenceFilter.requestKey, [value], false);
                },
                onClear: () => {
                    clearFilterKey(filter.presenceFilter.requestKey, false);
                }
            };
        }
        let appliedPresenceFilter = (filter.presenceFilter?.requestKey) ? filters[filter.presenceFilter?.requestKey] : null;
        return {
            ...col,
            key: `unused-${filter.requestKey}`,
            filterMultiple: false,
            filteredValue: filters[filter.requestKey] ?? appliedPresenceFilter,
            filterDropdownProps: {
                onOpenChange: (visible) => {
                    setOpenFilterKey(visible ? filter.requestKey : undefined);
                }
            },
            filterDropdown: (props) => (_jsx(TextTableFilter, { defaultValue: filters[filter.requestKey], placeholder: filter.placeholder, forceUppercase: filter.forceUppercase, onApplyFilter: (value) => {
                    applyFilterKey(filter.requestKey, value);
                    props.close();
                }, onClearFilter: () => {
                    clearFilterKey(filter.requestKey);
                    props.close();
                }, presenceFilter: presenceFilterOpts }))
        };
    };
    const mapNumberFilter = (col) => {
        const filter = col.filter;
        return {
            ...col,
            key: `unused-${filter.requestKey}`,
            filterMultiple: false,
            filteredValue: filters[filter.requestKey],
            filterDropdownProps: {
                onOpenChange: (visible) => {
                    setOpenFilterKey(visible ? filter.requestKey : undefined);
                }
            },
            filterDropdown: (props) => (_jsx(NumberTableFilter, { defaultValue: filters[filter.requestKey], placeholder: filter.placeholder, min: filter.min, max: filter.max, onApplyFilter: (value) => {
                    applyFilterKey(filter.requestKey, value);
                    props.close();
                }, onClearFilter: () => {
                    clearFilterKey(filter.requestKey);
                    props.close();
                } }))
        };
    };
    const mapDateRangeFilter = (col) => {
        const filter = col.filter;
        return {
            ...col,
            key: `unused-${filter.requestKey}`,
            filterMultiple: false,
            filteredValue: filters[filter.requestKey],
            filterDropdownProps: {
                onOpenChange: (visible) => {
                    setOpenFilterKey(visible ? filter.requestKey : undefined);
                }
            },
            filterDropdown: (props) => (_jsx(DateRangeTableFilter, { defaultFromValue: filters[filter.requestKey], defaultToValue: filters[filter.requestKeyDateTo], onApplyFilter: (fromDate, toDate) => {
                    applyFilterKeys({
                        [filter.requestKey]: fromDate,
                        [filter.requestKeyDateTo]: toDate
                    });
                    props.close();
                }, onClearFilter: () => {
                    clearFilterKeys([filter.requestKey, filter.requestKeyDateTo]);
                    props.close();
                } }))
        };
    };
    const mapDateFilter = (col) => {
        const filter = col.filter;
        return {
            ...col,
            key: `unused-${filter.requestKey}`,
            filterMultiple: false,
            filteredValue: filters[filter.requestKey],
            filterDropdownProps: {
                onOpenChange: (visible) => {
                    setOpenFilterKey(visible ? filter.requestKey : undefined);
                }
            },
            filterDropdown: (props) => (_jsx(DateTableFilter, { placeholder: filter.placeholder, defaultValue: filters[filter.requestKey], onApplyFilter: (value) => {
                    applyFilterKey(filter.requestKey, value);
                    props.close();
                }, onClearFilter: () => {
                    clearFilterKey(filter.requestKey);
                    props.close();
                } }))
        };
    };
    const mapNumberMinMaxFilter = (col) => {
        const filter = col.filter;
        return {
            ...col,
            key: `unused-${filter.requestKey}`,
            filterMultiple: false,
            filteredValue: filters[filter.requestKey] || filters[filter.requestKeyMax],
            filterDropdownProps: {
                onOpenChange: (visible) => {
                    setOpenFilterKey(visible ? filter.requestKey : undefined);
                }
            },
            filterDropdown: (props) => (_jsx(NumberMinMaxTableFilter, { defaultMin: filters[filter.requestKey], defaultMax: filters[filter.requestKeyMax], onApplyFilter: (min, max) => {
                    applyFilterKeys({
                        [filter.requestKey]: min,
                        [filter.requestKeyMax]: max
                    });
                    props.close();
                }, onClearFilter: () => {
                    clearFilterKeys([filter.requestKey, filter.requestKeyMax]);
                    props.close();
                } }))
        };
    };
    const mapFilter = (col) => {
        switch (col.filter?.type) {
            case "enum": return mapEnumsFilter(col);
            case "text": return mapTextFilter(col);
            case "dateRange": return mapDateRangeFilter(col);
            case "date": return mapDateFilter(col);
            case "numberMinMax": return mapNumberMinMaxFilter(col);
            case "number": return mapNumberFilter(col);
            case "custom": return col.filter.builder(col, filters, openFilterKey, setOpenFilterKey, applyFilterKeys, clearFilterKeys);
        }
        return col;
    };
    const filteredColumns = () => {
        return columns
            .filter(col => userPermissionFilter(loggedInUser, col))
            .filter(conditionFilter)
            .map(mapFilter);
    };
    const transformExpandable = () => {
        if (!expandable) {
            return undefined;
        }
        return {
            ...expandable,
            expandedRowKeys,
            onExpand: onRowExpand,
        };
    };
    return (_jsx(Table, { id: tableKey, components: components, className: className, columns: filteredColumns(), rowClassName: rowClassName, size: size, onRow: onRow, rowKey: rowKey, dataSource: data, pagination: pagination, loading: showSpinner || loading, onChange: onTableChange, scroll: { x: minWidthBeforeScrolling }, rowSelection: rowSelection, expandable: transformExpandable(), summary: summary }));
};
export default React.forwardRef(PaginatedFilterableTable);
